/**
 * Пользовательские поля внутри блока карточки (§7, §4.4).
 *
 * Поле из конструктора обязательно привязано к одному из четырёх блоков
 * (`config.block`); если создатель блок не выбрал — поле попадает в «Сервис и
 * заметки» (§7). Этот компонент рисует такие поля строками 44 px рядом с
 * «родными» полями блока, с той же разметкой подписи и теми же правилами
 * черновика.
 *
 * Модуль — нижний слой карточки: здесь живут примитивы, которыми пользуются
 * и ChannelBlock, и CategoriesBlock (пустота значения, право на правку,
 * подпись значения справочника). Зависимостей на другие файлы карточки у него
 * нет — поэтому циклов импорта в модуле не возникает.
 */
import { useEffect, useRef, useState } from 'react'
import type {
  Brand, Change, ChannelCode, CurrentUser, EnumValue, FieldDefinition, UserSummary,
} from '@shared/types.ts'
import {
  Avatar, Checkbox, Input, Popover, Select, type SelectOption,
} from '../../../components/index.js'
import { customFieldCell, type Cell, type Overlay } from '../../../lib/draft-overlay.js'
import { withEnumBadges } from '../table/enum-badges.js'
import { useApp } from '../../../store/app-store.js'
import { cardTexts } from './texts.card.js'
import './FieldsBlock.css'

// ─────────────────────────────── модель блоков ───────────────────────────────
/** Четыре смысловых блока карточки (§7). Та же модель, что в колонках и фильтрах. */
export type CardBlockId = 'site' | 'priority' | 'promo' | 'service'

const BLOCK_IDS: readonly CardBlockId[] = ['site', 'priority', 'promo', 'service']

/** Блок пользовательского поля: `config.block`, иначе «Сервис и заметки» (§7). */
export function blockOfField(field: FieldDefinition): CardBlockId {
  const config: unknown = field.config
  const raw =
    config && typeof config === 'object' && 'block' in config
      ? (config as { block?: unknown }).block
      : null
  return BLOCK_IDS.find((id) => id === raw) ?? 'service'
}

// ─────────────────────────── примитивы значений ──────────────────────────────
/** Пусто — это задача, а не значение (П3): пустая строка тоже пусто. */
export function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

export interface EditPermission {
  allowed: boolean
  /** Причина отказа — она всегда видна строкой рядом, тултипа мало (§11.4). */
  reason: string | null
}

/**
 * Редактируемость = роль × поле × пустая/заполненная (§11.4, критерий №9).
 * «Пополняющий» заполняет пустое и не меняет заполненное; наблюдатель не правит
 * ничего. Право на поле целиком сервер проверяет ещё раз.
 */
export function canEditValue(user: CurrentUser | null, isEmptyNow: boolean): EditPermission {
  if (!user) return { allowed: false, reason: null }
  if (user.permissions.includes('brands.edit')) return { allowed: true, reason: null }
  if (user.permissions.includes('brands.create')) {
    return isEmptyNow
      ? { allowed: true, reason: null }
      : { allowed: false, reason: cardTexts.deny.filledForContributor }
  }
  return { allowed: false, reason: cardTexts.deny.viewer }
}

/** Подпись значения справочника: 'yes' → «Да», 'A+' → «A+». */
export function enumLabelOf(
  enums: Record<string, EnumValue[]> | undefined,
  setCode: string,
  value: unknown,
): string | null {
  if (typeof value !== 'string' || value === '') return null
  const found = enums?.[setCode]?.find((item) => item.value === value)
  return found?.label ?? value
}

/** Опции справочника для `<Select>`: чип значения такой же, как в таблице. */
export function enumOptions(
  enums: Record<string, EnumValue[]> | undefined,
  setCode: string,
): SelectOption[] {
  const values = enums?.[setCode] ?? []
  return withEnumBadges(
    values.filter((item) => item.isActive).map((item) => ({
      value: item.value,
      label: item.label,
      ...(item.hint ? { hint: item.hint } : null),
    })),
    setCode,
    enums,
  )
}

/** «было: B» / «было: пусто» под контролом (§5.5). */
export function wasLine(label: string | null): string {
  return label === null || label === '' ? cardTexts.value.wasEmpty : cardTexts.value.was(label)
}

// ───────────────────────── пользовательские поля ─────────────────────────────
export function customFieldsFor(
  fields: FieldDefinition[] | undefined,
  blockId: CardBlockId,
): FieldDefinition[] {
  return (fields ?? [])
    .filter((field) => field.status === 'active')
    .filter((field) => field.scope === 'brand' || field.scope === 'brand_channel')
    .filter((field) => blockOfField(field) === blockId)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'ru'))
}

/**
 * Значение пользовательского поля с учётом черновика.
 *
 * Поля со `scope = brand` адресуются по id бренда — работает штатная
 * `customFieldCell`. Полям со `scope = brand_channel` сервер адресует правку по
 * id строки `brand_channels`, которого в срезе нет (см. отчёт модуля), поэтому
 * черновик для них ищется по адресу правки: бренд + канал + поле.
 */
export function customCell(
  overlay: Overlay,
  draftChanges: Change[],
  brand: Brand,
  channel: ChannelCode,
  field: FieldDefinition,
): Cell<unknown> {
  if (field.scope === 'brand') {
    return customFieldCell(overlay, 'brand', brand.id, field.id, field.code, brand.fields)
  }
  const source = brand.channels[channel]?.fields ?? {}
  const published = source[field.code] ?? null
  const change = draftChanges.find(
    (item) =>
      item.entityType === 'field_value' &&
      item.key.fieldId === field.id &&
      item.key.brandId === brand.id &&
      item.key.channelCode === channel,
  )
  if (!change) return { value: published, published, isDraft: false, label: null, change: null }
  return {
    value: change.newValue,
    published,
    isDraft: true,
    label: change.newLabel,
    change,
  }
}

export interface FieldsBlockProps {
  brand: Brand
  channel: ChannelCode
  blockId: CardBlockId
  /** Панель только на чтение: бренд архивирован или прав нет вовсе. */
  readonly?: boolean
}

export function FieldsBlock({ brand, channel, blockId, readonly = false }: FieldsBlockProps) {
  const fields = useApp((state) => state.bootstrap?.fields)
  const list = customFieldsFor(fields, blockId)
  if (list.length === 0) return null

  return (
    <>
      {list.map((field) => (
        <CustomFieldRow
          key={field.id}
          field={field}
          brand={brand}
          channel={channel}
          readonly={readonly}
        />
      ))}
    </>
  )
}

interface CustomFieldRowProps {
  field: FieldDefinition
  brand: Brand
  channel: ChannelCode
  readonly: boolean
}

function CustomFieldRow({ field, brand, channel, readonly }: CustomFieldRowProps) {
  const overlay = useApp((state) => state.overlay)
  const draftChanges = useApp((state) => state.draftChanges)
  const user = useApp((state) => state.bootstrap?.user ?? null)
  const enums = useApp((state) => state.bootstrap?.enums)
  const users = useApp((state) => state.bootstrap?.users ?? [])
  const editBrand = useApp((state) => state.editBrand)
  const editChannel = useApp((state) => state.editChannel)

  const cell = customCell(overlay, draftChanges, brand, channel, field)
  const permission = canEditValue(user, isEmptyValue(cell.published))
  const editable = permission.allowed && !readonly

  function commit(next: unknown): void {
    const values = { [field.code]: next }
    if (field.scope === 'brand') void editBrand(brand.id, { fields: values })
    else void editChannel(brand.id, channel, { fields: values })
  }

  const rowClasses = ['card-field']
  if (cell.isDraft) rowClasses.push('card-field--draft')

  return (
    <div className={rowClasses.join(' ')}>
      <span className="card-field__label t-label">{field.name}</span>
      <div className="card-field__control">
        <CustomFieldControl
          field={field}
          cell={cell}
          enums={enums}
          users={users}
          disabled={!editable}
          onCommit={commit}
        />
        {cell.isDraft ? (
          <span className="card-field__was t-meta">
            {wasLine(labelOfCustom(field, cell.published, enums, users))}
          </span>
        ) : null}
        {!editable && permission.reason ? (
          <span className="card-field__reason t-meta">{permission.reason}</span>
        ) : null}
        {field.hint ? <span className="card-field__hint t-meta">{field.hint}</span> : null}
      </div>
    </div>
  )
}

function labelOfCustom(
  field: FieldDefinition,
  value: unknown,
  enums: Record<string, EnumValue[]> | undefined,
  users: UserSummary[],
): string | null {
  if (isEmptyValue(value)) return null
  if (field.type === 'bool') return value === true ? cardTexts.value.yes : cardTexts.value.no
  if (field.type === 'select') {
    const option = field.config.options?.find((item) => item.value === value)
    if (option) return option.label
    return field.config.enumSet ? enumLabelOf(enums, field.config.enumSet, value) : String(value)
  }
  if (field.type === 'multiselect' && Array.isArray(value)) {
    return value
      .map((item) => field.config.options?.find((option) => option.value === item)?.label ?? String(item))
      .join(', ')
  }
  if (field.type === 'user') {
    const found = users.find((item) => String(item.id) === String(value))
    return found?.shortName ?? String(value)
  }
  return String(value)
}

interface CustomFieldControlProps {
  field: FieldDefinition
  cell: Cell<unknown>
  enums: Record<string, EnumValue[]> | undefined
  users: UserSummary[]
  disabled: boolean
  onCommit: (value: unknown) => void
}

function CustomFieldControl({
  field, cell, enums, users, disabled, onCommit,
}: CustomFieldControlProps) {
  const [open, setOpen] = useState(false)

  switch (field.type) {
    case 'bool':
      return (
        <Checkbox
          checked={cell.value === true}
          onChange={(checked) => onCommit(checked)}
          disabled={disabled}
          label={field.name}
        />
      )

    case 'select': {
      const options = field.config.options
        ? field.config.options.map((item) => ({ value: item.value, label: item.label }))
        : enumOptions(enums, field.config.enumSet ?? '')
      return (
        <Select
          value={typeof cell.value === 'string' ? cell.value : null}
          options={options}
          onChange={(next) => onCommit(next)}
          open={open}
          onOpenChange={setOpen}
          label={field.name}
          emptyLabel={cardTexts.value.empty}
          clearLabel={field.isRequired ? '' : cardTexts.value.clear}
          allowEmpty={!field.isRequired}
          disabled={disabled}
        />
      )
    }

    case 'user': {
      const options = users
        .filter((item) => item.isActive)
        .map((item) => ({
          value: String(item.id),
          label: item.shortName,
          badge: <Avatar name={item.fullName} size={18} />,
        }))
      return (
        <Select
          value={cell.value == null ? null : String(cell.value)}
          options={options}
          onChange={(next) => onCommit(next === null ? null : Number(next))}
          open={open}
          onOpenChange={setOpen}
          label={field.name}
          emptyLabel={cardTexts.value.empty}
          clearLabel={cardTexts.value.clear}
          disabled={disabled}
        />
      )
    }

    case 'multiselect':
      return (
        <MultiSelectField
          field={field}
          value={Array.isArray(cell.value) ? cell.value.map(String) : []}
          disabled={disabled}
          onCommit={onCommit}
        />
      )

    case 'number':
      return (
        <CommitInput
          value={cell.value == null ? '' : String(cell.value)}
          disabled={disabled}
          numeric
          inputMode="numeric"
          label={field.name}
          placeholder={field.config.placeholder}
          validate={(raw) => {
            if (raw.trim() === '') return null
            const parsed = Number(raw.replace(',', '.'))
            if (!Number.isFinite(parsed)) return cardTexts.custom.notANumber
            const { min, max } = field.config
            if (min !== undefined && max !== undefined && (parsed < min || parsed > max)) {
              return cardTexts.custom.outOfRange(min, max)
            }
            if (min !== undefined && parsed < min) return cardTexts.custom.outOfRange(min, max ?? min)
            if (max !== undefined && parsed > max) return cardTexts.custom.outOfRange(min ?? 0, max)
            return null
          }}
          onCommit={(raw) => onCommit(raw.trim() === '' ? null : Number(raw.replace(',', '.')))}
        />
      )

    case 'date':
      return (
        <CommitInput
          value={typeof cell.value === 'string' ? cell.value : ''}
          disabled={disabled}
          type="date"
          label={field.name}
          onCommit={(raw) => onCommit(raw === '' ? null : raw)}
        />
      )

    case 'url':
      return (
        <CommitInput
          value={typeof cell.value === 'string' ? cell.value : ''}
          disabled={disabled}
          label={field.name}
          placeholder={field.config.placeholder}
          onCommit={(raw) => onCommit(raw.trim() === '' ? null : raw.trim())}
        />
      )

    case 'text':
    case 'textarea':
    default:
      return (
        <CommitInput
          value={typeof cell.value === 'string' ? cell.value : ''}
          disabled={disabled}
          label={field.name}
          placeholder={field.config.placeholder}
          onCommit={(raw) => onCommit(raw.trim() === '' ? null : raw)}
        />
      )
  }
}

// ─────────────────────── поле с фиксацией по Enter/blur ──────────────────────
export interface CommitInputProps {
  value: string
  label: string
  /** id контрола: по нему к полю привязывается подпись строки. */
  id?: string
  disabled?: boolean
  numeric?: boolean
  type?: 'text' | 'date'
  inputMode?: 'numeric'
  placeholder?: string
  validate?: (raw: string) => string | null
  onCommit: (raw: string) => void
}

/**
 * Текстовое поле карточки: правка фиксируется по Enter и по уходу фокуса.
 * Дебаунса здесь нет — в отличие от ячейки таблицы (§5.2), в карточке человек
 * печатает целую строку и ждёт, что она уйдёт в черновик одной правкой.
 */
export function CommitInput({
  value, label, id, disabled = false, numeric = false, type = 'text',
  inputMode, placeholder, validate, onCommit,
}: CommitInputProps) {
  const [draft, setDraft] = useState(value)
  const [error, setError] = useState<string | null>(null)
  const committed = useRef(value)

  /* Пришло новое значение извне (черновик, обновление данных) — показываем его,
     если человек не правит поле прямо сейчас. */
  useEffect(() => {
    if (committed.current !== value) {
      committed.current = value
      setDraft(value)
      setError(null)
    }
  }, [value])

  function commit(): void {
    if (draft === value) return
    const problem = validate?.(draft) ?? null
    setError(problem)
    if (problem) return
    committed.current = draft
    onCommit(draft)
  }

  return (
    <Input
      id={id}
      value={draft}
      type={type}
      inputMode={inputMode}
      numeric={numeric}
      placeholder={placeholder}
      disabled={disabled}
      aria-label={label}
      error={error ?? undefined}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          commit()
        }
        if (event.key === 'Escape') {
          setDraft(value)
          setError(null)
        }
      }}
    />
  )
}

interface MultiSelectFieldProps {
  field: FieldDefinition
  value: string[]
  disabled: boolean
  onCommit: (value: unknown) => void
}

function MultiSelectField({ field, value, disabled, onCommit }: MultiSelectFieldProps) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement | null>(null)
  const options = field.config.options ?? []
  const selected = options.filter((option) => value.includes(option.value))

  function toggle(code: string, checked: boolean): void {
    const next = checked ? [...value, code] : value.filter((item) => item !== code)
    onCommit(next)
  }

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="card-multi__trigger t-cell"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={field.name}
        aria-disabled={disabled || undefined}
        onClick={() => {
          if (!disabled) setOpen((previous) => !previous)
        }}
      >
        {selected.length === 0
          ? cardTexts.value.empty
          : selected.map((option) => option.label).join(', ')}
      </button>
      <Popover
        open={open}
        anchor={anchor.current}
        onClose={() => setOpen(false)}
        role="dialog"
        matchAnchorWidth
        minWidth={200}
        maxWidth={280}
      >
        <div className="card-multi__list">
          {options.map((option) => (
            <Checkbox
              key={option.value}
              checked={value.includes(option.value)}
              onChange={(checked) => toggle(option.value, checked)}
              label={option.label}
            />
          ))}
        </div>
      </Popover>
    </>
  )
}
